import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { PrefetchService } from './modules/shared-platform/services/prefetch.service';
import { SubSink } from 'subsink';
import { BehaviorSubject, map, mergeMap } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { UserService } from './modules/admin/services/user.service';
import { MixpanelService } from './services/mixpanel.service';
import { AuthCacheService } from './services/auth-cache.service';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HeaderComponent } from './components/header-component/header.component';
import { FooterComponent } from './components/footer-component/footer.component';
import { SpinnerLoaderComponent } from './modules/shared-platform/components/spinner-loader/spinner-loader.component';
import { AsyncPipe } from '@angular/common';

// Extend Performance interface to include memory
interface PerformanceMemory {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
}

interface ExtendedPerformance extends Performance {
    memory?: PerformanceMemory;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        HeaderComponent,
        RouterOutlet,
        FooterComponent,
        SpinnerLoaderComponent,
        AsyncPipe,
    ],
})
export class AppComponent implements OnDestroy, OnInit {
    private prefetchService = inject(PrefetchService);
    private authService = inject(AuthService);
    private userService = inject(UserService);
    private mixpanelService = inject(MixpanelService);
    private router = inject(Router);
    private authCacheService = inject(AuthCacheService);

    public isLoading$ = new BehaviorSubject<boolean>(true);
    private sub = new SubSink();

    constructor() {
        this.sub.sink = this.authCacheService.init();

        this.sub.sink = this.prefetchService.prefetch().subscribe();

        this.sub.sink = this.authService.isLoading$
            .pipe(
                mergeMap((isLoading) => {
                    return this.userService.getCurrentUser().pipe(
                        map((user) => {
                            if (user) {
                                localStorage.setItem('userEmail', user?.email);
                                this.mixpanelService.init(user?.email);
                            }
                            return isLoading || !user;
                        }),
                    );
                }),
            )
            .subscribe((loading) => {
                this.isLoading$.next(loading);
            });
    }

    ngOnInit() {
        // Monitor route changes for performance
        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd,
                ),
            )
            .subscribe((event: NavigationEnd) => {
                // Log route change timing
                const timing = window.performance.timing;
                const navigationStart = timing.navigationStart;
                const now = Date.now();
                const loadTime = now - navigationStart;

                console.log(
                    `Route change to ${event.urlAfterRedirects} took ${loadTime}ms`,
                );

                // Send performance metrics to analytics
                const performance = window.performance as ExtendedPerformance;
                if (performance && performance.memory) {
                    console.log('Memory usage:', {
                        usedJSHeapSize: performance.memory.usedJSHeapSize,
                        totalJSHeapSize: performance.memory.totalJSHeapSize,
                        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
                    });
                }
            });

        // Monitor long tasks
        if ('PerformanceObserver' in window) {
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (entry.duration > 50) {
                        // Log tasks longer than 50ms
                        console.log('Long task detected:', entry);
                    }
                }
            });
            observer.observe({ entryTypes: ['longtask'] });
        }
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    title = 'Rail Performance';
}
